// import { isEmpty } from 'lodash';
import { approvalStateKeys } from '~/components/Budget/Forms/addNewBudgetFormUtils';
import { formatDateToISOString, toDateTime } from '~/functions/dateTimeUtilities';
import type { AddNewExpenseFormState, GrantsAndBudgetAllocation } from '~/types/expenses';

export const addNewExpenseBreadCrumb: { route: string; title: string }[] = [
  { route: '/expenses', title: 'Expenses' },
  { route: '/expenses/add-new-expense', title: 'New Expense' }
];

export enum BookOfAccountOptions {
  FCRA = 'FCRA',
  NONFCRA = 'Non-FCRA'
}

export enum ExpenseAttachmentCategories {
  INVOICE = 'Invoice',
  PO = 'PO',
  DELIVERY_PROOF = 'Delivery Proof',
  SERVICE_COMPLETION_PROOF = 'Service Completion Proof',
  OTHERS = 'Others'
}

export enum ProjectExpenses {
  ProformaInvoice = 'Proforma Invoice/quotation/Tax invoice',
  PurchaseOrder = 'Purchase order/Work Order/SLA',
  Procurement_approval = "Procurement Approval/George's approval on exception case",
  Annexure_I = 'Annexure I_Vendor Declaration form',
  Annexure_IV = 'Annexure IV_Format for quote comparison and vendor recommendation',
  Annexure_II = 'Annexure II_RFQ Format',
  Annexure_III = 'Annexure III_Quotation Format',
  Copy_of_pan = 'Copy of PAN',
  Cancelled_cheque = 'Cancelled cheque'
}

export const MandatoryProjectAttachments = [
  ProjectExpenses.ProformaInvoice
  // ProjectExpenses.PurchaseOrder,
  // ProjectExpenses.Procurement_approval,
  // ProjectExpenses.Annexure_I
] as const;

export enum NGOSub_GrantsTab {
  MOU_agreement_LOI = 'MOU/Agreement/LOI',
  Addendum_if_any = 'Addendum if any'
}

export const MandatoryNGOProjectAttachments = [NGOSub_GrantsTab.MOU_agreement_LOI] as const;

// export type AttachmentCategoryOption = {
//   label: string;
//   value: string;
//   required?: boolean;
// };

export const expenseFormDefaultState: AddNewExpenseFormState = {
  expenseType: '',
  invoiceDate: null,
  partyName: '',
  invoiceNumber: '',
  payeeBank: '',
  description: '',
  totalAmount: null,
  approversList: {},
  draftStatus: false,
  creator: {
    email: '',
    first_name: '',
    last_name: ''
  },
  firstApproval: '',
  secondApproval: '',
  thirdApproval: '',
  fourthApproval: '',
  fifthApproval: '',
  sixthApproval: '',
  seventhApproval: '',
  eighthApproval: '',
  ninthApproval: '',
  tenthApproval: '',
  grantsAndBudgetAllocation: [
    {
      key: Date.now(),
      expenseBudget: 'Unallocated',
      expenseBudgetAmount: 0,
      expenseLineItem: 'Unallocated',
      expenseBudgetLineItemAmount: 0,
      expenseGrants: 'Unallocated',
      expenseGrantsAmount: 0,
      grantsLineItem: 'Unallocated',
      expenseGrantsLineItemAmount: 0,
      purpose: '',
      notes: '',
      isEditing: false,
      isReadMore: false,
      attachment: [],
      allocationAmount: null,
      validationError: '',
      accountInfo: {
        bookOfAccount: '',
        expenseLedger: 'Unallocated',
        partyLedger: 'Unallocated',
        tdsLedger: 'Not Applicable',
        tdsAmount: 0,
        assessableAmount: 0,
        bankAccount: ''
      }
    }
  ],
  approval_status: '',
  attachments: [],
  approvalCount: 0,
  totalAllocationAmount: 0,
  table: '',
  auditLogs: [],
  attachment_logs: [],
  tax_invoice: false,
  payment_stats: {
    amount_paid: '',
    balance_for_fcra: '',
    balance_for_non_fcra: '',
    balance_payments: '',
    payable: '',
    remaining_to_be_allocated: '',
    tds: '',
    total_amount: '',
    balance: ''
  }
};

export const newExpenseRules = {
  expenseType: [{ required: true, trigger: 'submit', message: 'Please select expense type' }],
  invoiceDate: [{ required: true, trigger: 'submit', message: 'Please select invoice date' }],
  partyName: [{ required: true, trigger: 'submit', message: 'Please enter party name' }],
  invoiceNumber: [{ required: true, trigger: 'submit', message: 'Please enter invoice number' }],
  totalAmount: [{ required: true, trigger: 'submit', message: 'Please enter total amount' }],
  firstApproval: [{ required: true, trigger: 'submit', message: 'Please select first approval' }],
  secondApproval: [{ required: true, trigger: 'submit', message: 'Please select second approval' }],
  thirdApproval: [{ required: true, trigger: 'submit', message: 'Please select third approval' }],
  fourthApproval: [{ required: true, trigger: 'submit', message: 'Please select fourth approval' }],
  fifthApproval: [{ required: true, trigger: 'submit', message: 'Please select fifth approval' }],
  sixthApproval: [{ required: true, trigger: 'submit', message: 'Please select sixth approval' }],
  seventhApproval: [{ required: true, trigger: 'submit', message: 'Please select seventh approval' }],
  eighthApproval: [{ required: true, trigger: 'submit', message: 'Please select eighth approval' }],
  ninthApproval: [{ required: true, trigger: 'submit', message: 'Please select ninth approval' }],
  tenthApproval: [{ required: true, trigger: 'submit', message: 'Please select tenth approval' }]
};

export const createBudgetOptions = (budgetOption: any) => {
  return budgetOption.map((budget: any) => ({
    mainLabel: budget.budget_name,
    subLabel: budget.budget_id,
    value: budget.budget_id_value,
    amount: budget.amount - (budget.utilization.approved + budget.utilization.not_approved),
    lineItem: budget.line_items.map((lineItem: any) => ({
      label: `${lineItem.data.li} - ${lineItem.data.sub_li}`,
      value: lineItem.data.li_value,
      amount: lineItem.amount - (lineItem.utilized.approved + lineItem.utilized.not_approved),
      is_show_amount: budget.is_show_amount
    })),
    is_show_amount: budget.is_show_amount
  }));
};

export const createGrantOptions = (grantOption: any) => {
  return grantOption.map((grant: any) => ({
    mainLabel: grant.grant_id,
    subLabel: grant.donor_name,
    value: grant.grant_id_value,
    amount: grant.amount - (grant.utilization.approved + grant.utilization.not_approved),
    purpose: grant.purpose,
    fcraStatus: grant.is_fcra,
    bankAccount: grant.bank_account || '',
    lineItem: grant.line_items.map((lineItem: any) => ({
      label: `${lineItem.data.li} - ${lineItem.data.sub_li}`,
      value: lineItem.data.li_value,
      amount: lineItem.amount - (lineItem.utilized.approved + lineItem.utilized.not_approved),
      is_show_amount: Boolean(grant.is_show_amount)
    })),
    is_show_amount: Boolean(grant.is_show_amount)
  }));
};

export const allocationValidation = (expenseAllocation: GrantsAndBudgetAllocation[]) => {
  const validationErrorsArr = expenseAllocation.map(item => {
    const error = [];
    if (item.allocationAmount === null || item.allocationAmount === undefined || item.allocationAmount < 0) {
      error.push('allocation amount');
    }
    if (!item.purpose) {
      error.push('purpose');
    }
    const errorMessage = error.length === 0 ? '' : `Please enter ${error.join(' and ')}!`;

    return {
      key: item.key,
      error: errorMessage
    };
  });
  return validationErrorsArr;
};

// const approvalListData = (approversList: string[]) => {
//   const formattedData: Record<string, any> = {};
//   approversList.forEach((approver, index) => {
//     const itemNumber = index + 1;
//     if (isEmpty(approver)) return;
//     formattedData[itemNumber] = {
//       [`expense_approver_${itemNumber}`]: approver,
//       [`approver_status_${itemNumber}`]: 'Pending',
//       [`approver_notes_${itemNumber}`]: ''
//     };
//   });
//   return JSON.stringify(formattedData).toString();
// };

const buildApproversJson = (formState: AddNewExpenseFormState, existingApproversList: any) => {
  const updatedList: Record<string, any> = {};

  const approverValues = [
    formState.firstApproval,
    formState.secondApproval,
    formState.thirdApproval,
    formState.fourthApproval,
    formState.fifthApproval,
    formState.sixthApproval,
    formState.seventhApproval,
    formState.eighthApproval,
    formState.ninthApproval,
    formState.tenthApproval
  ];

  approverValues.forEach((approver, index) => {
    const key = (index + 1).toString();

    if (!approver) return; // skip empty slots

    const existing = existingApproversList?.[key];
    const oldApprover = existing?.[`expense_approver_${index + 1}`];

    if (oldApprover && oldApprover === approver) {
      updatedList[key] = {
        [`expense_approver_${index + 1}`]: approver,
        [`approver_status_${index + 1}`]: existing[`approver_status_${index + 1}`],
        [`approver_notes_${index + 1}`]: existing[`approver_notes_${index + 1}`]
      };
    } else {
      updatedList[key] = {
        [`expense_approver_${index + 1}`]: approver,
        [`approver_status_${index + 1}`]: 'Pending',
        [`approver_notes_${index + 1}`]: ''
      };
    }
  });
  return JSON.stringify(updatedList);
};

export const createNewExpenseFormData = (
  formState: AddNewExpenseFormState,
  existingApproversList?: any,
  enterpriseVersion?: boolean
) => {
  const approversList = buildApproversJson(formState, existingApproversList);

  const formData: Record<string, any> = {
    category: formState.expenseType,
    invoice_date: formState.invoiceDate ? formatDateToISOString(formState.invoiceDate) : '',
    vendor_bank: formState.payeeBank,
    party_name: formState.partyName,
    invoice_no: formState.invoiceNumber,
    amount: formState.totalAmount,
    description: formState.description,
    approvers_json: approversList
  };

  if (enterpriseVersion) {
    formData.tax_invoice = formState.tax_invoice;
  }

  let attachmentIndex = 1;

  formState.attachments.forEach(file => {
    formData[`at_cat_${attachmentIndex}`] = file.category;
    if (file.rawFile) {
      formData[`at_file_${attachmentIndex}`] = file.rawFile;
    }
    attachmentIndex++;
  });

  const lineItemData: Record<string, any> = {};
  formState.grantsAndBudgetAllocation.forEach((allocation, index) => {
    const itemNumber = index + 1;
    lineItemData[itemNumber] = {
      [`li_budget_${itemNumber}`]: allocation.expenseBudget,
      [`li_budget_li_${itemNumber}`]: allocation.expenseLineItem,
      [`li_grant_id_${itemNumber}`]: allocation.expenseGrants,
      [`li_grant_li_${itemNumber}`]: allocation.grantsLineItem,
      [`li_purpose_${itemNumber}`]: allocation.purpose,
      [`li_amount_${itemNumber}`]: allocation.allocationAmount,
      [`li_note_${itemNumber}`]: allocation.notes,
      [`li_fcra_status_${itemNumber}`]: allocation.accountInfo.bookOfAccount,
      [`li_ledger_${itemNumber}`]: allocation.accountInfo.expenseLedger,
      [`li_party_ledger_name_${itemNumber}`]: allocation.accountInfo.partyLedger,
      [`li_tds_ledger_name_${itemNumber}`]: allocation.accountInfo.tdsLedger,
      [`li_tds_amount_${itemNumber}`]: allocation.accountInfo.tdsAmount || 0,
      [`li_assessable_amount_${itemNumber}`]: allocation.accountInfo.assessableAmount || 0,
      [`li_bank_account_${itemNumber}`]: allocation.accountInfo.bankAccount || ''
    };

    allocation.attachment.forEach(attachment => {
      formData[`at_file_${attachmentIndex}`] = attachment.rawFile;
      formData[`at_cat_${attachmentIndex}`] = '';
      formData[`at_lineitem_${attachmentIndex}`] = itemNumber;
      attachmentIndex++;
    });
  });

  formData.line_items = JSON.stringify(lineItemData);

  return formData;
};

const getApprovalStatus = (approvalStatus: string) => {
  if (approvalStatus === '0 of 0' || approvalStatus === 'Approved') {
    return 'approved';
  } else if (approvalStatus === 'Not submitted for approval') {
    return 'draft';
  } else {
    return 'pending';
  }
};

export const parseFormDataToState = (formData: Record<string, any>, editPage: boolean, enterpriseVersion: boolean) => {
  const formState: any = {
    expenseType: formData.category,
    invoiceDate: formData.invoice_date ? toDateTime(formData.invoice_date) : null,
    partyName: formData.party_name,
    payeeBank: formData.vendor_bank,
    invoiceNumber: formData.invoice_no,

    totalAmount: formData.amount,
    description: formData.description,
    approversList: formData.approvers_list,
    draftStatus: formData.draft_status,
    approval_status: formData.approval_status,
    isCurrentApprover: formData.is_current_approver,
    creator: {
      email: formData.creator.email,
      first_name: formData.creator.first_name,
      last_name: formData.creator.last_name
    },
    attachments: []
  };

  if (enterpriseVersion) {
    formState.tax_invoice = formData.tax_invoice;
  }

  const approvalValues = Object.values(formData.approvers_list).map(
    (approver: any, index: number) => approver[`expense_approver_${index + 1}`]
  );
  let approvalCount = 0;
  Object.values(approvalStateKeys).forEach((key: string, index) => {
    if (approvalValues[index]) approvalCount++;
    formState[key as keyof AddNewExpenseFormState] = approvalValues[index] || '';
  });
  formState.approvalCount = approvalCount;

  formState.grantsAndBudgetAllocation = formData.line_items.map((item: any, index: number) => ({
    key: Date.now() + index,
    expenseBudget: item.li_budget,
    expenseLineItem: item.li_budget_li,
    expenseGrants: item.li_grant_id,
    grantsLineItem: item.li_grant_li,
    purpose: item.li_purpose,
    allocationAmount: +item.li_amount,
    notes: item.li_note,
    isEditing: false,
    isReadMore: false,
    validationError: '',
    attachment: [],
    accountInfo: {
      bookOfAccount: item.li_fcra_status,
      expenseLedger: item.li_ledger,
      partyLedger: item.li_party_ledger_name,
      tdsLedger: item.li_tds_ledger_name,
      tdsAmount: +item.li_tds_amount,
      assessableAmount: +item.li_assessable_amount,
      bankAccount: item.li_bank_account || ''
    }
  }));

  formState.table = formData.table;
  // Real payment stats sent by the backend (total invoice amount, TDS, payable, amount
  // paid, balance) for the "Expense Payment Info" summary card.
  formState.payment_stats = formData.payment_stats || expenseFormDefaultState.payment_stats;

  // Also add attachments to the grants and budget allocation.
  // This might need to be removed in the future as there is some difficulties to store in the BE.
  if (editPage) {
    formData.attachments.forEach((att: any) => {
      if (att.line_item_no !== '') {
        formState.grantsAndBudgetAllocation[att.line_item_no - 1].attachment.push({
          filename: att.filename
        });
      } else {
        formState.attachments.push({
          filename: att.filename,
          category: att.category
        });
      }
    });
    formState.auditLogs = formData.audit_logs;
    formState.attachment_logs = formData.attachment_logs;
    formState.approvalStatus = getApprovalStatus(formData.approval_status);
    formState.approval_status = getApprovalStatus(formData.approval_status);
    formState.isCurrentApprover = formData.is_current_approver ?? false;
    formState.isFinalApprover = formData.is_final_approver;
  }

  return formState;
};
